import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  BadgeCheck,
  Briefcase,
  Building2,
  Calendar,
  Check,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  EyeOff,
  FileLock2,
  FileText,
  Menu,
  Pencil,
  Send,
  ShieldCheck,
  User,
  UserCheck,
  X,
} from 'lucide-react';
import { STATS } from './forRecruitersStats';
import { FAQ_ITEMS } from './forRecruitersFaqData';
import Footer from '../components/Footer';
import CareerGrowthLogo from '../components/CareerGrowthLogo';

const SIGNUP_PATH = '/b2b/auth?signup=1';
const SIGNIN_PATH = '/b2b/auth';

export default function ForRecruitersLandingScreen() {
  return (
    <div className="min-h-screen bg-canvas text-ink-1 font-sans">
      <Nav />
      <main>
        <Hero />
        <StatsBar />
        <HowItWorks />
        <QAAsyncScreening />
        <QAComparison />
        <UseCases />
        <Compliance />
        <Pricing />
        <FaqAccordion />
        <FinalCta />
      </main>
      <Footer />
    </div>
  );
}

/* ─────────────────────────  HEADER  ───────────────────────── */

function Nav() {
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (!isOpen) return undefined;
    const { overflow } = document.body.style;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = overflow;
    };
  }, [isOpen]);

  useEffect(() => {
    const handleKey = (e) => e.key === 'Escape' && setIsOpen(false);
    if (isOpen) document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isOpen]);

  const close = () => setIsOpen(false);

  return (
    <>
      <header className="sticky top-0 z-40 bg-white/80 backdrop-blur border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-5 md:px-6 py-3 md:py-4 flex items-center justify-between">
          <Link to="/for-recruiters" className="flex items-center gap-3 min-w-0">
            <CareerGrowthLogo size={36} />
            <span className="hidden sm:inline-flex text-[10px] uppercase tracking-wider font-bold text-accent border border-accent/30 rounded-full px-2 py-0.5">
              For recruiters
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-1">
            <Link to={SIGNIN_PATH} className="text-sm font-semibold text-ink-2 hover:text-ink-1 px-3 py-2 rounded-lg transition">
              Sign in
            </Link>
            <Link
              to={SIGNUP_PATH}
              className="ml-2 inline-flex items-center gap-1.5 text-sm font-bold bg-accent hover:bg-accent-hover text-white px-4 py-2.5 rounded-2xl transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2"
            >
              Start free
              <ArrowRight className="w-4 h-4" aria-hidden="true" />
            </Link>
          </div>

          <button
            type="button"
            onClick={() => setIsOpen(true)}
            className="md:hidden h-10 w-10 rounded-xl border border-gray-200 bg-white flex items-center justify-center text-ink-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2"
            aria-label="Open menu"
            aria-expanded={isOpen}
            aria-controls="for-recruiters-mobile-menu"
          >
            <Menu className="w-5 h-5" aria-hidden="true" />
          </button>
        </div>
      </header>

      {/* Backdrop */}
      <div
        className={`md:hidden fixed inset-0 z-[1000] bg-black/45 transition-opacity duration-300 ${
          isOpen ? 'opacity-100' : 'pointer-events-none opacity-0'
        }`}
        onClick={close}
        aria-hidden={!isOpen}
      />

      {/* Slide-in panel */}
      <aside
        id="for-recruiters-mobile-menu"
        className={`md:hidden fixed right-0 top-0 bottom-0 z-[1001] w-[min(280px,75vw)] bg-white shadow-2xl transition-transform duration-300 ease-[cubic-bezier(0.32,0.72,0,1)] flex flex-col ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <CareerGrowthLogo size={28} />
            <span className="text-[10px] uppercase tracking-wider font-bold text-accent border border-accent/30 rounded-full px-2 py-0.5">
              For recruiters
            </span>
          </div>
          <button
            onClick={close}
            className="w-9 h-9 flex items-center justify-center bg-gray-100 rounded-lg text-ink-1 hover:bg-gray-200 transition"
            aria-label="Close menu"
          >
            <X className="w-[18px] h-[18px]" aria-hidden="true" />
          </button>
        </div>

        <nav className="flex-1 px-3 py-3 overflow-y-auto">
          <Link
            to={SIGNIN_PATH}
            onClick={close}
            className="w-full flex items-center gap-3 p-3 rounded-xl text-left text-[15px] font-medium text-ink-2 hover:bg-gray-50 transition group"
          >
            <span className="flex-1">Sign in</span>
            <ChevronRight className="w-4 h-4 text-ink-3" aria-hidden="true" />
          </Link>
        </nav>

        <div className="p-4 pb-6 border-t border-gray-100">
          <Link
            to={SIGNUP_PATH}
            onClick={close}
            className="w-full inline-flex items-center justify-center gap-2 bg-accent hover:bg-accent-hover text-white py-3.5 rounded-2xl text-[15px] font-semibold transition shadow-sm"
          >
            Start free
            <ArrowRight className="w-4 h-4" aria-hidden="true" />
          </Link>
        </div>
      </aside>
    </>
  );
}

/* ─────────────────────────  HERO  ───────────────────────── */

function Hero() {
  return (
    <section className="max-w-6xl mx-auto px-5 md:px-6 py-12 md:py-20">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
        <div className="lg:col-span-6 order-2 lg:order-1">
          <p className="text-xs font-bold uppercase tracking-wider text-accent">
            AI-assisted candidate screening · For agencies &amp; SMB recruiters
          </p>
          <h1 className="mt-4 text-4xl md:text-5xl lg:text-[56px] font-bold leading-[1.08] tracking-tight">
            Get a{' '}
            <span className="text-accent">ranked, client-ready shortlist</span>
            {' '}— without a single screening call
          </h1>
          <p className="mt-5 text-base md:text-lg text-ink-2 leading-relaxed max-w-xl">
            JobSkills generates senior-level questions for each role and CV. Candidates
            answer async through a link, and you get a structured scorecard on every one —
            strengths, risks, and the quotes behind them. Compare and rank in one place.
            No calls, no scheduling.
          </p>
          <div className="mt-7 flex flex-wrap items-center gap-3">
            <Link
              to={SIGNUP_PATH}
              className="inline-flex items-center gap-2 bg-accent hover:bg-accent-hover text-white font-bold px-6 py-3.5 rounded-2xl text-base transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2"
            >
              Start free — 10 screenings on us
              <ArrowRight className="w-4 h-4" aria-hidden="true" />
            </Link>
            <a
              href="#how-it-works"
              className="inline-flex items-center gap-2 border border-gray-200 bg-white hover:border-gray-300 text-ink-1 font-bold px-6 py-3.5 rounded-2xl text-base transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2"
            >
              See how it works
            </a>
          </div>
          <ul className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-ink-3">
            {['No credit card', 'Cancel anytime', 'Set up in 4 minutes'].map((label) => (
              <li key={label} className="inline-flex items-center gap-1.5">
                <CheckCircle2 className="w-3.5 h-3.5 text-accent" aria-hidden="true" />
                {label}
              </li>
            ))}
          </ul>
        </div>

        <div className="lg:col-span-6 order-1 lg:order-2">
          <HeroMockup />
        </div>
      </div>
    </section>
  );
}

function HeroMockup() {
  const candidates = [
    {
      initial: 'A',
      tone: 'bg-accent/15 text-accent',
      name: 'Anna K.',
      role: 'Senior Frontend',
      score: 87,
      scoreTone: 'bg-emerald-100 text-emerald-700',
      answers: [
        { q: 'Years with React?', a: '5 years', good: true },
        { q: 'Salary expectation?', a: '$85K–95K', good: true },
        { q: 'Notice period?', a: '4 weeks', good: false },
      ],
    },
    {
      initial: 'D',
      tone: 'bg-blue-100 text-blue-700',
      name: 'David R.',
      role: 'Senior Frontend',
      score: 74,
      scoreTone: 'bg-amber-100 text-amber-700',
      answers: [
        { q: 'Years with React?', a: '3 years', good: true },
        { q: 'Salary expectation?', a: '$110K', good: false },
        { q: 'Notice period?', a: '2 weeks', good: true },
      ],
    },
  ];

  return (
    <div className="relative">
      <div
        aria-hidden="true"
        className="absolute -right-8 -top-8 h-40 w-40 rounded-full bg-accent/15 blur-2xl -z-10"
      />
      <div
        aria-hidden="true"
        className="absolute -left-6 -bottom-6 h-32 w-32 rounded-full bg-blue-300/20 blur-2xl -z-10"
      />
      <div className="relative bg-white rounded-2xl border border-gray-200 shadow-card p-4 md:p-5">
        <div className="flex items-center justify-between border-b border-gray-100 pb-3">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-ink-3">Screening</p>
            <p className="text-sm font-semibold text-ink-1">Senior Frontend Engineer</p>
          </div>
          <span className="text-xs text-ink-3 font-medium bg-gray-50 border border-gray-100 rounded-full px-2.5 py-1">
            2 candidates
          </span>
        </div>

        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
          {candidates.map((c) => (
            <div
              key={c.name}
              className="rounded-xl border border-gray-100 bg-canvas/60 p-3.5 flex flex-col"
            >
              <div className="flex items-center gap-2.5">
                <div className={`w-9 h-9 rounded-full flex items-center justify-center font-bold text-sm ${c.tone}`}>
                  {c.initial}
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-ink-1 truncate">{c.name}</p>
                  <p className="text-[11px] text-ink-3 truncate">{c.role}</p>
                </div>
              </div>
              <div className="mt-3 space-y-2">
                {c.answers.map((a) => (
                  <div key={a.q} className="text-[11px]">
                    <p className="text-ink-3">{a.q}</p>
                    <p className="mt-0.5 flex items-center gap-1 font-semibold text-ink-1">
                      {a.good ? (
                        <Check className="w-3 h-3 text-emerald-600 shrink-0" aria-hidden="true" />
                      ) : (
                        <span className="w-3 h-3 inline-flex items-center justify-center text-amber-600 shrink-0" aria-hidden="true">!</span>
                      )}
                      {a.a}
                    </p>
                  </div>
                ))}
              </div>
              <div className="mt-3 pt-3 border-t border-gray-100 flex items-center justify-between">
                <span className="text-[10px] uppercase tracking-wider font-bold text-ink-3">Match</span>
                <span className={`text-xs font-bold rounded-full px-2 py-0.5 ${c.scoreTone}`}>{c.score}%</span>
              </div>
            </div>
          ))}
        </div>

        <p className="mt-4 text-[11px] text-ink-3 text-center">+ 5 more questions per candidate</p>
      </div>
    </div>
  );
}

/* ─────────────────────────  STATS  ───────────────────────── */

function StatsBar() {
  return (
    <section className="max-w-6xl mx-auto px-5 md:px-6 pb-12 md:pb-20">
      <p className="text-xs font-bold uppercase tracking-wider text-accent text-center">
        The screening problem in numbers
      </p>
      <h2 className="mt-3 text-2xl md:text-4xl font-bold text-center max-w-3xl mx-auto leading-tight">
        Manual screening costs more than you think
      </h2>
      <div className="mt-10 bg-white rounded-3xl border border-gray-200 shadow-card p-6 md:p-10 grid grid-cols-1 md:grid-cols-3 md:divide-x divide-gray-100 gap-y-8 md:gap-y-0">
        {STATS.map((s) => (
          <div key={s.id} className="px-2 md:px-6 text-center">
            <div className="text-5xl md:text-6xl font-bold text-ink-1 leading-none">{s.number}</div>
            <p className="mt-3 text-sm text-ink-2 leading-snug max-w-[220px] mx-auto">{s.label}</p>
            <a
              href={s.sourceUrl}
              target="_blank"
              rel="noopener noreferrer nofollow"
              className="mt-2 inline-block text-[11px] text-ink-3 hover:text-accent transition"
              aria-label={`Source: ${s.sourceLabel}`}
            >
              [{s.id}] {s.sourceLabel}
            </a>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ─────────────────────────  HOW IT WORKS  ───────────────────────── */

const HOW_IT_WORKS_STEPS = [
  {
    icon: FileText,
    title: 'Add the role and a CV',
    body: "Paste the role description and the candidate's CV. About two minutes per candidate.",
    visual: 'job-candidate',
  },
  {
    icon: Pencil,
    title: 'AI writes the screening questions',
    body: 'JobSkills generates senior-level questions tailored to that exact role and CV — the kind a domain expert would ask. Review, tweak, or add your own.',
    visual: 'questions',
  },
  {
    icon: Send,
    title: 'Send the link, get a scorecard',
    body: "Each candidate gets a unique link and answers async. You get a structured scorecard per candidate — strengths, risks, quotes — ranked side-by-side. No scheduling, no scattered notes.",
    visual: 'review',
  },
];

function HowItWorks() {
  return (
    <section id="how-it-works" className="bg-white border-y border-gray-100">
      <div className="max-w-6xl mx-auto px-5 md:px-6 py-16 md:py-24">
        <p className="text-xs font-bold uppercase tracking-wider text-accent">How it works</p>
        <h2 className="mt-3 text-3xl md:text-4xl font-bold max-w-3xl leading-tight">
          From sourced CV to a scored shortlist in three steps
        </h2>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-4">
          {HOW_IT_WORKS_STEPS.map((s, i) => {
            const Icon = s.icon;
            return (
              <div
                key={s.title}
                className="relative bg-canvas/40 rounded-2xl border border-gray-200 p-5 md:p-6 flex flex-col"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-accent/10 text-accent flex items-center justify-center">
                    <Icon className="w-5 h-5" aria-hidden="true" />
                  </div>
                  <span className="text-xs font-bold uppercase tracking-wider text-ink-3">
                    Step {i + 1}
                  </span>
                </div>
                <h3 className="mt-4 text-lg font-bold text-ink-1">{s.title}</h3>
                <p className="mt-2 text-sm text-ink-2 leading-relaxed">{s.body}</p>
                <div className="mt-5">
                  <StepVisual variant={s.visual} />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function StepVisual({ variant }) {
  if (variant === 'job-candidate') {
    return (
      <div className="space-y-2">
        <div className="bg-white rounded-lg border border-gray-200 p-3">
          <p className="text-[10px] uppercase tracking-wider font-bold text-ink-3">Job</p>
          <p className="text-xs font-semibold text-ink-1 mt-0.5">Senior Frontend Engineer</p>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-3">
          <p className="text-[10px] uppercase tracking-wider font-bold text-ink-3">Candidate CV</p>
          <p className="text-xs font-semibold text-ink-1 mt-0.5">Anna K. — 5y React, Berlin</p>
        </div>
      </div>
    );
  }
  if (variant === 'questions') {
    return (
      <ol className="bg-white rounded-lg border border-gray-200 p-3 space-y-1.5">
        {[
          'Years with React?',
          'Salary expectation?',
          'Notice period?',
        ].map((q, i) => (
          <li key={q} className="flex items-start gap-2 text-xs text-ink-1">
            <span className="text-[10px] font-bold text-ink-3 mt-0.5">{i + 1}.</span>
            <span>{q}</span>
          </li>
        ))}
        <li className="text-[10px] text-ink-3 pl-4">+ 5 more</li>
      </ol>
    );
  }
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-3">
      <div className="flex items-center justify-between text-[10px]">
        <span className="font-semibold text-ink-1">Anna K.</span>
        <span className="font-bold text-emerald-600">87%</span>
      </div>
      <div className="mt-2 flex items-center justify-between text-[10px]">
        <span className="font-semibold text-ink-1">David R.</span>
        <span className="font-bold text-amber-600">74%</span>
      </div>
      <div className="mt-2 flex items-center justify-between text-[10px]">
        <span className="font-semibold text-ink-3">Marek P.</span>
        <span className="font-bold text-ink-3">48%</span>
      </div>
    </div>
  );
}

/* ─────────────────────────  Q&A: WHAT IS IT  ───────────────────────── */

function QAAsyncScreening() {
  return (
    <section className="max-w-6xl mx-auto px-5 md:px-6 py-16 md:py-20">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12">
        <div>
          <p className="text-xs font-bold uppercase tracking-wider text-accent">FAQ · cited by AI search</p>
          <h2 className="mt-3 text-2xl md:text-3xl font-bold leading-tight">
            What is async candidate screening?
          </h2>
        </div>
        <p
          data-testid="qa-async-screening-answer"
          className="md:col-span-2 text-base md:text-lg text-ink-2 leading-relaxed"
        >
          <strong className="text-ink-1">Async candidate screening</strong> is a hiring practice
          where candidates answer screening questions on their own schedule, rather than on a
          synchronous phone or video call. With JobSkills, the questions are generated by AI for
          each role and CV (the recruiter can edit them); the candidate responds within a deadline;
          and the recruiter reviews a structured scorecard for each candidate side-by-side —
          strengths, risks, and quotes — instead of subjective notes. It replaces the 30-minute
          screening call with a ~15-minute written questionnaire and produces comparable,
          client-ready artifacts.
        </p>
      </div>
    </section>
  );
}

/* ─────────────────────────  COMPARISON TABLE  ───────────────────────── */

const COMPARISON_ROWS = [
  { tool: 'HireVue / Modern Hire', cost: '$25K–100K/yr', completion: '40–60%', artifact: 'Video recording' },
  { tool: 'Spark Hire', cost: '$149–499/mo', completion: '50–65%', artifact: 'Video recording' },
  { tool: 'Phone screen', cost: '~$15–21/screen', completion: '~90%', artifact: 'Recruiter notes' },
  {
    tool: 'Jobskills',
    cost: '$1–2/screen',
    completion: 'Higher — candidates get feedback',
    artifact: 'Structured scorecard + export',
    highlight: true,
  },
];

function QAComparison() {
  return (
    <section className="bg-white border-y border-gray-100">
      <div className="max-w-6xl mx-auto px-5 md:px-6 py-16 md:py-24">
        <p className="text-xs font-bold uppercase tracking-wider text-accent">Comparison</p>
        <h2 className="mt-3 text-2xl md:text-3xl font-bold max-w-3xl leading-tight">
          How is Jobskills different from HireVue, Spark Hire, and other video screening tools?
        </h2>
        <p className="mt-4 text-base text-ink-2 max-w-3xl leading-relaxed">
          Most video screening platforms (HireVue, Spark Hire, Modern Hire) record candidate video
          answers and price for enterprise volume — typically{' '}
          <strong className="text-ink-1">$25,000–100,000 per year</strong>. Jobskills is text-based
          async screening priced per generated questionnaire (
          <strong className="text-ink-1">$1–2 per screen</strong>), built for recruiting agencies
          and SMB in-house recruiters who want behavioural-style screening at SMB economics, not
          enterprise.
        </p>

        {/* Desktop table */}
        <div className="hidden md:block mt-10 rounded-2xl border border-gray-200 overflow-hidden shadow-card">
          <table aria-label="Screening tool comparison" className="min-w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-left text-[11px] font-bold uppercase tracking-wider text-ink-3">
                <th className="py-3 px-5">Tool</th>
                <th className="py-3 px-5">Cost</th>
                <th className="py-3 px-5">Completion rate</th>
                <th className="py-3 px-5">Candidate-facing artifact</th>
              </tr>
            </thead>
            <tbody>
              {COMPARISON_ROWS.map((r) => (
                <tr
                  key={r.tool}
                  className={`border-t border-gray-100 ${r.highlight ? 'bg-accent/5' : 'bg-white'}`}
                >
                  <td className={`py-4 px-5 font-semibold text-ink-1 ${r.highlight ? 'border-l-4 border-accent' : ''}`}>
                    {r.tool}
                  </td>
                  <td className="py-4 px-5 text-ink-2">{r.cost}</td>
                  <td className="py-4 px-5 text-ink-2">{r.completion}</td>
                  <td className="py-4 px-5">
                    {r.highlight ? (
                      <span className="font-semibold text-accent">{r.artifact}</span>
                    ) : (
                      <span className="text-ink-3">{r.artifact}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile cards */}
        <div className="md:hidden mt-8 space-y-3">
          {COMPARISON_ROWS.map((r) => (
            <div
              key={r.tool}
              className={`rounded-2xl border p-4 ${r.highlight ? 'border-accent bg-accent/5' : 'border-gray-200 bg-white'}`}
            >
              <p className={`font-semibold ${r.highlight ? 'text-accent' : 'text-ink-1'}`}>{r.tool}</p>
              <dl className="mt-3 grid grid-cols-2 gap-3 text-xs">
                <div>
                  <dt className="text-ink-3 uppercase tracking-wider font-bold text-[10px]">Cost</dt>
                  <dd className="mt-1 text-ink-1 font-medium">{r.cost}</dd>
                </div>
                <div>
                  <dt className="text-ink-3 uppercase tracking-wider font-bold text-[10px]">Completion</dt>
                  <dd className="mt-1 text-ink-1 font-medium">{r.completion}</dd>
                </div>
                <div className="col-span-2">
                  <dt className="text-ink-3 uppercase tracking-wider font-bold text-[10px]">Artifact</dt>
                  <dd className={`mt-1 font-medium ${r.highlight ? 'text-accent' : 'text-ink-2'}`}>{r.artifact}</dd>
                </div>
              </dl>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────  USE CASES  ───────────────────────── */

const USE_CASES = [
  {
    icon: Building2,
    title: 'Recruiting agencies',
    bestFit: '5+ vacancies in parallel, contingency fee model',
    body: 'Agency consultants run several vacancies in parallel, screening ~30 LinkedIn-sourced candidates per role. Phone screens cap throughput at ~5 per day. With Jobskills async screening, one consultant can run noticeably more vacancies in parallel and hand clients a structured, scored shortlist instead of verbal "vibes" notes.',
  },
  {
    icon: Briefcase,
    title: 'In-house SMB recruiters',
    bestFit: '1–2 person team, 3–10 open roles',
    body: 'Solo or small in-house teams hire 3–10 roles concurrently and cannot justify a $30K/yr enterprise screening platform. Jobskills replaces Excel + Gmail + LinkedIn Recruiter Lite with a structured screening layer at $39–149 per month — an order of magnitude cheaper.',
  },
  {
    icon: User,
    title: 'Freelance recruiters',
    bestFit: 'Independent / contingency, throughput-driven income',
    body: 'Independent recruiters work on contingency — throughput directly drives income. Jobskills lets one recruiter reach far more candidates per week without losing evaluation quality. A structured scorecard becomes a sellable artifact you hand to the hiring manager.',
  },
];

function UseCases() {
  return (
    <section className="max-w-6xl mx-auto px-5 md:px-6 py-16 md:py-24">
      <p className="text-xs font-bold uppercase tracking-wider text-accent">Who Jobskills is built for</p>
      <h2 className="mt-3 text-3xl md:text-4xl font-bold max-w-3xl leading-tight">
        Built for recruiting agencies, SMB in-house teams, and freelance recruiters
      </h2>
      <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
        {USE_CASES.map((u) => {
          const Icon = u.icon;
          return (
            <div
              key={u.title}
              className="bg-white rounded-2xl border border-gray-200 p-6 shadow-card hover:shadow-card-hover transition flex flex-col"
            >
              <div className="w-11 h-11 rounded-xl bg-accent/10 text-accent flex items-center justify-center">
                <Icon className="w-5 h-5" aria-hidden="true" />
              </div>
              <h3 className="mt-4 text-lg font-bold text-ink-1">{u.title}</h3>
              <p className="mt-3 text-sm text-ink-2 leading-relaxed">{u.body}</p>
              <div className="mt-5 pt-4 border-t border-gray-100">
                <p className="text-[10px] font-bold uppercase tracking-wider text-ink-3">Best fit if</p>
                <p className="mt-1 text-xs text-ink-2">{u.bestFit}</p>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

/* ─────────────────────────  COMPLIANCE  ───────────────────────── */

const COMPLIANCE_PILLARS = [
  {
    icon: EyeOff,
    title: 'No biometric scoring',
    body: 'We don’t analyze faces, voices, or "confidence levels." Structured text questions, structured text answers — outside EU AI Act Art 5(1)(f) emotion-recognition prohibition.',
    cite: 'EU AI Act Art 5(1)(f)',
  },
  {
    icon: UserCheck,
    title: 'AI recommends, your recruiter decides',
    body: 'Fit verdicts come with reasoning and evidence. There is no automated hire/reject. The recruiter scores every candidate. Keeps you outside fully-automated decision-making.',
    cite: 'GDPR Art 22',
  },
  {
    icon: FileLock2,
    title: 'Tamper-evident audit trail',
    body: 'Every AI call is logged with SHA-256 hashes of inputs and outputs, model version, latency, and outcome — audit-grade record without retaining the full content.',
    cite: 'EU AI Act Art 12 · GDPR Art 30',
  },
  {
    icon: BadgeCheck,
    title: 'Versioned candidate consent',
    body: 'Candidates explicitly consent to AI processing before any analysis runs. When the policy version changes, re-consent is required — enforced at the database level.',
    cite: 'GDPR Art 7 · EU AI Act Art 50',
  },
  {
    icon: Calendar,
    title: 'Configurable data retention',
    body: 'Choose 6, 12, 24, or 36 months per workspace. After the window, candidate data is automatically deleted. Audit logs retained 18 months for compliance, then purged.',
    cite: 'GDPR Art 5(1)(e)',
  },
  {
    icon: FileText,
    title: 'Compliance pack at signup',
    body: 'DPA (with SCCs Module 2 + UK IDTA), AI System Card, sub-processor list, breach runbook, and DPIA template — all customer-accessible from day one. No procurement back-and-forth.',
    cite: 'GDPR Art 28 · EU AI Act Art 50',
  },
];

function Compliance() {
  return (
    <section
      id="compliance"
      data-testid="for-recruiters-compliance-section"
      className="bg-white border-y border-gray-100"
    >
      <div className="max-w-6xl mx-auto px-5 md:px-6 py-16 md:py-24">
        <p className="text-xs font-bold uppercase tracking-wider text-accent">
          Compliance · EU AI Act ready
        </p>
        <h2 className="mt-3 text-3xl md:text-4xl font-bold max-w-3xl leading-tight">
          Built for the EU AI Act, not retrofitted for it.
        </h2>
        <p className="mt-5 text-base md:text-lg text-ink-2 max-w-3xl leading-relaxed">
          The EU AI Act enters its high-risk phase for hiring AI on{' '}
          <strong className="text-ink-1">2 August 2026</strong>. Most AI hiring tools were
          designed before this regulation existed. Jobskills was designed for it —
          closed-ended text screening, no biometrics, human-in-the-loop by default.
        </p>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
          {COMPLIANCE_PILLARS.map((p) => {
            const Icon = p.icon;
            return (
              <div
                key={p.title}
                className="bg-white rounded-2xl border border-gray-200 p-6 shadow-card hover:shadow-card-hover transition flex flex-col"
              >
                <div className="w-11 h-11 rounded-xl bg-accent/10 text-accent flex items-center justify-center">
                  <Icon className="w-5 h-5" aria-hidden="true" />
                </div>
                <h3 className="mt-4 text-base font-bold text-ink-1">{p.title}</h3>
                <p className="mt-3 text-sm text-ink-2 leading-relaxed flex-1">{p.body}</p>
                {p.cite && (
                  <p className="mt-4 pt-4 border-t border-gray-100 text-[10px] uppercase tracking-wider font-bold text-ink-3">
                    {p.cite}
                  </p>
                )}
              </div>
            );
          })}
        </div>

        <div className="mt-10 rounded-2xl bg-canvas border border-gray-200 p-6 md:p-8">
          <div className="flex items-start gap-4">
            <div className="hidden md:flex w-12 h-12 shrink-0 rounded-xl bg-accent/10 text-accent items-center justify-center">
              <ShieldCheck className="w-6 h-6" aria-hidden="true" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg md:text-xl font-bold text-ink-1">
                Get the Jobskills Compliance Pack
              </h3>
              <p className="mt-2 text-sm md:text-base text-ink-2 leading-relaxed">
                Data Processing Agreement, AI System Card, sub-processor list with transfer
                mechanisms, and a fillable DPIA template your DPO can complete. Everything
                your procurement team needs in one bundle.
              </p>
              <Link
                to="/b2b/compliance"
                className="mt-5 inline-flex items-center gap-1.5 text-sm font-bold bg-accent hover:bg-accent-hover text-white px-4 py-2.5 rounded-2xl transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2"
                data-testid="for-recruiters-compliance-cta"
              >
                See the full compliance breakdown
                <ArrowRight className="w-4 h-4" aria-hidden="true" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────  PRICING  ───────────────────────── */

const PLAN_FEATURES = [
  'AI-generated questions per role × CV (editable)',
  'Structured scorecard per candidate — strengths, risks, quotes',
  'Unlimited team members per workspace',
  'Side-by-side answer comparison & ranking',
  'Public candidate links — no login for them',
  'EU AI Act-ready: DPA, AI System Card, audit log included on all tiers',
];

const PRICING_PLANS = [
  {
    name: 'Free',
    tag: 'Try without commitment',
    price: '$0',
    cadence: '',
    quota: '10 screenings to start',
    cta: 'Start free',
    href: SIGNUP_PATH,
    primary: false,
  },
  {
    name: 'Starter',
    tag: 'Solo recruiter',
    price: '$39',
    cadence: '/mo',
    quota: '20 screenings / month',
    cta: 'Choose Starter',
    href: SIGNUP_PATH,
    primary: false,
  },
  {
    name: 'Agency',
    tag: 'Small agency',
    price: '$149',
    cadence: '/mo',
    quota: '100 screenings / month',
    cta: 'Choose Agency',
    href: SIGNUP_PATH,
    primary: true,
    badge: 'Most popular',
  },
  {
    name: 'Scale',
    tag: 'Mid-sized agency',
    price: '$349',
    cadence: '/mo',
    quota: '500 screenings / month',
    cta: 'Choose Scale',
    href: SIGNUP_PATH,
    primary: false,
  },
];

function Pricing() {
  return (
    <section className="bg-white border-y border-gray-100">
      <div className="max-w-6xl mx-auto px-5 md:px-6 py-16 md:py-24">
        <p className="text-xs font-bold uppercase tracking-wider text-accent">Pricing</p>
        <h2 className="mt-3 text-3xl md:text-4xl font-bold leading-tight">Pay per generated screening</h2>
        <p className="mt-3 text-base text-ink-2 max-w-2xl leading-relaxed">
          Every plan ships the same product — only the screening quota changes. No setup fees,
          no per-seat charges, no contracts.
        </p>

        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
          {PRICING_PLANS.map((p) => (
            <div
              key={p.name}
              className={`relative rounded-2xl bg-white p-6 flex flex-col ${
                p.primary
                  ? 'border-2 border-accent ring-4 ring-accent/10 shadow-card-hover'
                  : 'border border-gray-200 shadow-card'
              }`}
            >
              {p.badge && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-accent text-white text-[10px] font-bold uppercase tracking-wider px-3 py-1 rounded-full whitespace-nowrap">
                  {p.badge}
                </span>
              )}
              <p className="text-xs font-bold uppercase tracking-wider text-ink-3">{p.name}</p>
              <p className="mt-1 text-xs text-ink-3">{p.tag}</p>
              <div className="mt-4 flex items-baseline gap-1">
                <span className="text-4xl font-bold text-ink-1">{p.price}</span>
                {p.cadence && <span className="text-sm text-ink-3 font-medium">{p.cadence}</span>}
              </div>
              <p className="mt-2 text-sm font-semibold text-accent">{p.quota}</p>
              <ul className="mt-5 space-y-2.5 text-sm text-ink-2">
                {PLAN_FEATURES.map((f) => (
                  <li key={f} className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-accent shrink-0 mt-0.5" aria-hidden="true" />
                    <span className="leading-snug">{f}</span>
                  </li>
                ))}
              </ul>
              <Link
                to={p.href}
                className={`mt-6 inline-flex items-center justify-center gap-1.5 rounded-2xl px-4 py-2.5 text-sm font-bold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 ${
                  p.primary
                    ? 'bg-accent hover:bg-accent-hover text-white'
                    : 'border border-gray-200 text-ink-1 hover:border-accent hover:text-accent bg-white'
                }`}
              >
                {p.cta}
                <ArrowRight className="w-4 h-4" aria-hidden="true" />
              </Link>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────  FAQ  ───────────────────────── */

function FaqAccordion() {
  const [openIdx, setOpenIdx] = useState(null);
  return (
    <section className="max-w-3xl mx-auto px-5 md:px-6 py-16 md:py-24">
      <p className="text-xs font-bold uppercase tracking-wider text-accent text-center">Frequently asked</p>
      <h2 className="mt-3 text-3xl md:text-4xl font-bold text-center leading-tight">
        Everything you need to know
      </h2>
      <div className="mt-10 space-y-3">
        {FAQ_ITEMS.map((item, i) => {
          const open = openIdx === i;
          const panelId = `faq-panel-${i}`;
          const buttonId = `faq-button-${i}`;
          return (
            <div
              key={item.q}
              className={`rounded-xl border bg-white transition-colors ${
                open ? 'border-accent/40 shadow-card' : 'border-gray-200'
              }`}
            >
              <button
                id={buttonId}
                type="button"
                aria-expanded={open}
                aria-controls={panelId}
                onClick={() => setOpenIdx(open ? null : i)}
                className="w-full flex items-center justify-between gap-4 p-5 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 rounded-xl"
              >
                <h3 className="text-base md:text-lg font-bold text-ink-1 flex-1">{item.q}</h3>
                <ChevronDown
                  className={`w-5 h-5 text-ink-3 shrink-0 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
                  aria-hidden="true"
                />
              </button>
              {open && (
                <div id={panelId} role="region" aria-labelledby={buttonId} className="px-5 pb-5">
                  <p className="text-sm md:text-base text-ink-2 leading-relaxed">{item.a}</p>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}

/* ─────────────────────────  FINAL CTA  ───────────────────────── */

function FinalCta() {
  return (
    <section className="px-5 md:px-6 pb-16 md:pb-24">
      <div className="relative max-w-6xl mx-auto bg-ink-1 rounded-3xl px-6 py-12 md:px-16 md:py-20 text-center text-white overflow-hidden">
        <div
          aria-hidden="true"
          className="absolute -right-16 -top-16 h-64 w-64 rounded-full bg-accent/30 blur-3xl"
        />
        <div
          aria-hidden="true"
          className="absolute -left-20 -bottom-20 h-72 w-72 rounded-full bg-blue-500/20 blur-3xl"
        />
        <div className="relative">
          <h2 className="text-3xl md:text-5xl font-bold leading-tight">
            Free until you ship 10 screenings
          </h2>
          <p className="mt-5 text-base md:text-lg text-white/70 max-w-xl mx-auto leading-relaxed">
            No card required. Sign up, paste a CV, let AI draft the questions, send the link.
            You'll have your first candidate's scorecard tomorrow morning.
          </p>
          <Link
            to={SIGNUP_PATH}
            className="mt-8 inline-flex items-center gap-2 bg-accent hover:bg-accent-hover text-white font-bold px-8 py-4 rounded-2xl text-base md:text-lg transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-ink-1"
          >
            Start free
            <ArrowRight className="w-5 h-5" aria-hidden="true" />
          </Link>
          <p className="mt-4 text-xs text-white/50">No credit card · Cancel anytime · Set up in 4 minutes</p>
        </div>
      </div>
    </section>
  );
}
